import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Loader2,
  QrCode,
  ChevronLeft,
  ChevronRight,
  MapPin,
  ShieldAlert,
  Clock,
  AlertTriangle,
  Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  listActivitySites,
  getSiteDayActivity,
  getGuardDayScans,
  getCheckpointDayHistory,
} from "@/lib/site-checkpoints.functions";
import { cn } from "@/lib/utils";
import { SiteClockInMap, type ClockInPoint } from "@/components/SiteClockInMap";

function metersBetween(aLat: number, aLng: number, bLat: number, bLng: number) {
  const R = 6371000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const la1 = (aLat * Math.PI) / 180;
  const la2 = (bLat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export const Route = createFileRoute("/_authenticated/site-activity")({
  head: () => ({
    meta: [
      { title: "Site Activity — MissionReady" },
      {
        name: "description",
        content:
          "Review on-site checkpoint scans, guard coverage, and daily site activity for your organization.",
      },
    ],
  }),
  component: SiteActivityPage,
});

function todayIso() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 10);
}

function shiftDate(date: string, days: number) {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function SiteActivityPage() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const tz = useMemo(() => new Date().getTimezoneOffset(), []);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [date, setDate] = useState(todayIso());
  const [openGuard, setOpenGuard] = useState<string | null>(null);
  const [openCheckpoint, setOpenCheckpoint] = useState<string | null>(null);
  // Which punch marker is highlighted — shared by the guard list and the map.
  const [selectedPoint, setSelectedPoint] = useState<string | null>(null);
  const mapWrapRef = useRef<HTMLDivElement | null>(null);

  const fetchSites = useServerFn(listActivitySites);
  const fetchDay = useServerFn(getSiteDayActivity);
  const fetchGuard = useServerFn(getGuardDayScans);
  const fetchCheckpoint = useServerFn(getCheckpointDayHistory);

  const sitesQ = useQuery({
    queryKey: ["site-activity-sites", orgId],
    queryFn: () => fetchSites({ data: { orgId: orgId! } }),
    enabled: !!orgId,
  });

  const sites = sitesQ.data?.sites ?? [];
  const activeSite = siteId ?? sites[0]?.id ?? null;

  const dayQ = useQuery({
    queryKey: ["site-activity-day", orgId, activeSite, date, tz],
    queryFn: () =>
      fetchDay({ data: { orgId: orgId!, siteId: activeSite!, date, tzOffsetMinutes: tz } }),
    enabled: !!orgId && !!activeSite,
  });

  const guardQ = useQuery({
    queryKey: ["site-activity-guard", orgId, activeSite, date, openGuard, tz],
    queryFn: () =>
      fetchGuard({
        data: {
          orgId: orgId!,
          siteId: activeSite!,
          date,
          tzOffsetMinutes: tz,
          officerId: openGuard!,
        },
      }),
    enabled: !!orgId && !!activeSite && !!openGuard,
  });

  const cpQ = useQuery({
    queryKey: ["site-activity-checkpoint", orgId, activeSite, date, openCheckpoint, tz],
    queryFn: () =>
      fetchCheckpoint({
        data: {
          orgId: orgId!,
          siteId: activeSite!,
          date,
          tzOffsetMinutes: tz,
          checkpointId: openCheckpoint!,
        },
      }),
    enabled: !!orgId && !!activeSite && !!openCheckpoint,
  });

  // Real punch coordinates only — a punch without GPS never appears on the map.
  const clockInPoints = useMemo<ClockInPoint[]>(() => {
    const day = dayQ.data;
    if (!day) return [];
    const fences = day.geofences ?? [];
    const inside = (lat: number, lng: number) => {
      const inclusions = fences.filter((f) => f.kind === "inclusion");
      if (inclusions.length === 0) return null;
      return inclusions.some(
        (f) => metersBetween(lat, lng, f.center_lat, f.center_lng) <= f.radius_m,
      );
    };
    const out: ClockInPoint[] = [];
    for (const g of day.guards) {
      for (const p of g.punches) {
        if (p.clock_in_lat != null && p.clock_in_lng != null) {
          out.push({
            id: `${p.id}-in`,
            name: g.name,
            lat: p.clock_in_lat,
            lng: p.clock_in_lng,
            kind: "in",
            timeLabel: time(p.clock_in_at),
            accuracyM: p.clock_in_accuracy_m,
            onSiteNow: !p.clock_out_at,
            insidePerimeter: inside(p.clock_in_lat, p.clock_in_lng),
            note: p.flagged_reason,
          });
        }
        if (p.clock_out_at && p.clock_out_lat != null && p.clock_out_lng != null) {
          out.push({
            id: `${p.id}-out`,
            name: g.name,
            lat: p.clock_out_lat,
            lng: p.clock_out_lng,
            kind: "out",
            timeLabel: time(p.clock_out_at),
            insidePerimeter: inside(p.clock_out_lat, p.clock_out_lng),
          });
        }
      }
    }
    return out;
  }, [dayQ.data]);

  // Workspace clock-in boundary settings, so the map can show the effective
  // boundary even when a site has no explicit geofence drawn yet.
  const flagsQ = useQuery({
    queryKey: ["site-activity", "geofence-flags", orgId],
    queryFn: async () => {
      const { data } = await supabase
        .from("org_feature_flags")
        .select("time_clock_geofence_required, time_clock_geofence_radius_m")
        .eq("organization_id", orgId!)
        .maybeSingle();
      return {
        required: !!data?.time_clock_geofence_required,
        radiusM: Number(data?.time_clock_geofence_radius_m ?? 150),
      };
    },
    enabled: !!orgId,
    staleTime: 300_000,
  });
  const geofenceFlags = flagsQ.data;

  // Map a punch marker back to its guard so a pin click can open that row.
  const officerForPoint = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of dayQ.data?.guards ?? []) {
      for (const p of g.punches) {
        m.set(`${p.id}-in`, g.officer_id);
        m.set(`${p.id}-out`, g.officer_id);
      }
    }
    return m;
  }, [dayQ.data]);

  const selectFromList = (pointId: string) => {
    setSelectedPoint(pointId);
    mapWrapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (!orgId) return <Empty text="Select an organization to review site activity." />;
  if (sitesQ.isLoading) return <Spinner label="Loading sites…" />;
  if (sitesQ.error) return <Empty text="We couldn't load your sites. Please try again." />;
  if (sitesQ.data && !sitesQ.data.allowed)
    return <Empty text="You don't have access to site activity review." />;
  if (sites.length === 0) return <Empty text="No sites are assigned to you yet." />;

  const d = dayQ.data;

  return (
    <div className="space-y-4 pb-24">
      <header className="space-y-1">
        <h1 className="font-[Bebas_Neue] text-3xl tracking-wide text-white">SITE ACTIVITY</h1>
        <p className="text-sm text-[#C0C0C0]">
          On-site checkpoint scans, guard coverage, and daily events — no tour required.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <select
          value={activeSite ?? ""}
          onChange={(e) => {
            setSiteId(e.target.value);
            setOpenGuard(null);
            setOpenCheckpoint(null);
          }}
          className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
          aria-label="Site"
        >
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setDate((v) => shiftDate(v, -1))}
            className="rounded-md border border-white/10 p-2 text-white/80 hover:bg-white/10"
            aria-label="Previous day"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
            aria-label="Date"
          />
          <button
            type="button"
            onClick={() => setDate((v) => shiftDate(v, 1))}
            className="rounded-md border border-white/10 p-2 text-white/80 hover:bg-white/10"
            aria-label="Next day"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setDate(todayIso())}
            className="rounded-md border border-white/10 px-2 py-1.5 text-xs uppercase tracking-wide text-white/80 hover:bg-white/10"
          >
            Today
          </button>
        </div>
      </div>

      {dayQ.isLoading ? (
        <Spinner label="Loading activity…" />
      ) : dayQ.error ? (
        <Empty text="We couldn't load activity for this day. Please try again." />
      ) : !d ? null : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat icon={QrCode} label="Checkpoint scans" value={d.totals.scans} />
            <Stat icon={MapPin} label="Standalone scans" value={d.totals.standalone_scans} />
            <Stat icon={Users} label="Guards on site" value={d.totals.guards_on_site} />
            <Stat icon={Clock} label="Scheduled hours" value={d.totals.scheduled_hours} />
            <Stat icon={ShieldAlert} label="SOS alerts" value={d.totals.sos_alerts} />
            <Stat icon={AlertTriangle} label="Incidents" value={d.totals.incidents} />
            <Stat icon={QrCode} label="Tour scans" value={d.totals.tour_scans} />
            <Stat
              icon={MapPin}
              label="Checkpoints hit"
              value={`${d.totals.checkpoints_active}/${d.totals.checkpoints_total}`}
            />
          </div>

          <Section title="Clock-in map">
            {clockInPoints.length === 0 ? (
              <p className="p-3 text-sm text-[#C0C0C0]">
                No clock-in locations recorded for this day. Punches without GPS aren't shown on the
                map.
              </p>
            ) : (
              <div className="space-y-2 p-3" ref={mapWrapRef}>
                <SiteClockInMap
                  points={clockInPoints}
                  selectedId={selectedPoint}
                  onSelect={(id) => {
                    setSelectedPoint(id);
                    const officer = officerForPoint.get(id);
                    if (officer) setOpenGuard(officer);
                  }}
                  geofences={d.geofences ?? []}
                  siteCenter={
                    d.site?.latitude != null && d.site?.longitude != null
                      ? { lat: d.site.latitude, lng: d.site.longitude }
                      : null
                  }
                  siteName={d.site?.name ?? null}
                  fallbackRadiusM={geofenceFlags?.radiusM ?? null}
                />
                <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#C0C0C0]">
                  <li>
                    <span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500" />
                    Clock-in inside perimeter
                  </li>
                  <li>
                    <span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500" />
                    Clock-in outside perimeter
                  </li>
                  <li>
                    <span className="mr-1 inline-block h-2 w-2 rounded-full bg-orange-500" />
                    Clock-out
                  </li>
                  <li>Pulsing pin = still on site</li>
                  <li>
                    <span className="mr-1 inline-block h-2 w-2 rounded-full border border-cyan-400" />
                    Site boundary{" "}
                    {geofenceFlags?.required
                      ? "· clock-in requires being inside"
                      : "· clock-in outside is allowed and flagged"}
                  </li>
                  <li>Tap a guard's clock-in below to highlight its pin</li>
                </ul>
              </div>
            )}
          </Section>

          <Section title="Checkpoints">
            {d.checkpoints.length === 0 ? (
              <p className="p-3 text-sm text-[#C0C0C0]">
                No on-site checkpoints configured for this site yet.
              </p>
            ) : (
              <ul className="divide-y divide-white/10">
                {d.checkpoints.map((c) => {
                  const met = c.scan_count >= c.required_occurrences;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setOpenCheckpoint(openCheckpoint === c.id ? null : c.id)}
                        className="flex w-full items-center gap-3 p-3 text-left hover:bg-white/[0.04]"
                      >
                        <span
                          className={cn(
                            "h-2 w-2 rounded-full",
                            c.scan_count === 0
                              ? c.requirement === "required"
                                ? "bg-red-500"
                                : "bg-white/30"
                              : met
                                ? "bg-emerald-500"
                                : "bg-amber-400",
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-white">{c.name}</span>
                          <span className="block text-xs text-[#C0C0C0]">
                            {c.requirement === "required" ? "Required" : "Suggested"} ·{" "}
                            {c.scan_count}/{c.required_occurrences} today
                            {c.last_scanned_at
                              ? ` · last ${time(c.last_scanned_at)} by ${c.last_scanned_by}`
                              : " · not scanned"}
                          </span>
                        </span>
                        <ChevronRight
                          className={cn(
                            "h-4 w-4 shrink-0 text-white/40 transition",
                            openCheckpoint === c.id && "rotate-90",
                          )}
                        />
                      </button>
                      {openCheckpoint === c.id && (
                        <div className="border-t border-white/10 bg-black/30 px-3 py-2">
                          {c.manager_note && (
                            <p className="mb-2 text-xs text-amber-300/90">{c.manager_note}</p>
                          )}
                          {cpQ.isLoading ? (
                            <Spinner label="Loading scans…" small />
                          ) : (cpQ.data?.scans.length ?? 0) === 0 ? (
                            <p className="text-xs text-[#C0C0C0]">No scans recorded on this day.</p>
                          ) : (
                            <ul className="space-y-1">
                              {cpQ.data!.scans.map((s, i) => (
                                <li key={s.id} className="text-xs text-white/85">
                                  #{i + 1} · {time(s.scanned_at)} · {s.officer_name}
                                  {s.from_tour ? " · during tour" : " · on-site scan"}
                                  {s.inside_geofence === false ? " · outside geofence" : ""}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>

          <Section title="Guards">
            {d.guards.length === 0 ? (
              <p className="p-3 text-sm text-[#C0C0C0]">No guards scheduled or active this day.</p>
            ) : (
              <ul className="divide-y divide-white/10">
                {d.guards.map((g) => (
                  <li key={g.officer_id}>
                    <button
                      type="button"
                      onClick={() => {
                        const opening = openGuard !== g.officer_id;
                        setOpenGuard(opening ? g.officer_id : null);
                        const first = g.punches.find(
                          (p) => p.clock_in_lat != null && p.clock_in_lng != null,
                        );
                        if (opening && first) setSelectedPoint(`${first.id}-in`);
                        else if (!opening) setSelectedPoint(null);
                      }}
                      className={cn(
                        "flex w-full items-center gap-3 p-3 text-left hover:bg-white/[0.04]",
                        openGuard === g.officer_id && "bg-white/[0.04]",
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-white">{g.name}</span>
                        <span className="block text-xs text-[#C0C0C0]">
                          {g.shifts.length > 0
                            ? g.shifts
                                .map((s) => `${time(s.starts_at)}–${time(s.ends_at)}`)
                                .join(", ")
                            : "No scheduled shift"}
                          {" · "}
                          {g.scan_count} scans
                          {g.sos_count > 0 ? ` · ${g.sos_count} SOS` : ""}
                        </span>
                        {g.punches.length > 0 && (
                          <span className="mt-0.5 block text-xs text-emerald-300/90">
                            {g.punches
                              .map((p) =>
                                p.clock_out_at
                                  ? `Clocked in ${time(p.clock_in_at)} – out ${time(p.clock_out_at)}`
                                  : `Clocked in ${time(p.clock_in_at)} · on site now`,
                              )
                              .join(" · ")}
                          </span>
                        )}
                      </span>
                      <ChevronRight
                        className={cn(
                          "h-4 w-4 shrink-0 text-white/40 transition",
                          openGuard === g.officer_id && "rotate-90",
                        )}
                      />
                    </button>
                    {openGuard === g.officer_id && (
                      <div className="border-t border-white/10 bg-black/30 px-3 py-2">
                        {g.punches.length > 0 && (
                          <ul className="mb-2 space-y-1">
                            {g.punches.map((p) => (
                              <li
                                key={p.id}
                                className={cn(
                                  "rounded-md px-1.5 py-1 text-xs text-white/85",
                                  selectedPoint === `${p.id}-in` || selectedPoint === `${p.id}-out`
                                    ? "bg-[#cc0000]/20 ring-1 ring-[#cc0000]/50"
                                    : "",
                                )}
                              >
                                {p.clock_in_lat != null && p.clock_in_lng != null ? (
                                  <button
                                    type="button"
                                    onClick={() => selectFromList(`${p.id}-in`)}
                                    aria-pressed={selectedPoint === `${p.id}-in`}
                                    className="mr-1 rounded border border-white/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-white/80 hover:bg-white/10"
                                  >
                                    Show on map
                                  </button>
                                ) : null}
                                {p.clock_out_at && p.clock_out_lat != null && p.clock_out_lng != null ? (
                                  <button
                                    type="button"
                                    onClick={() => selectFromList(`${p.id}-out`)}
                                    aria-pressed={selectedPoint === `${p.id}-out`}
                                    className="mr-1 rounded border border-white/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-white/80 hover:bg-white/10"
                                  >
                                    Clock-out pin
                                  </button>
                                ) : null}
                                Clocked in {time(p.clock_in_at)}
                                {p.clock_out_at ? ` · clocked out ${time(p.clock_out_at)}` : " · still on site"}
                                {p.worked_minutes != null
                                  ? ` · ${Math.round((p.worked_minutes / 60) * 10) / 10} h`
                                  : ""}
                                {p.clock_in_lat != null && p.clock_in_lng != null ? (
                                  <>
                                    {" · "}
                                    <a
                                      href={`https://www.google.com/maps/search/?api=1&query=${p.clock_in_lat},${p.clock_in_lng}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="underline decoration-dotted"
                                    >
                                      {p.clock_in_lat.toFixed(5)}, {p.clock_in_lng.toFixed(5)}
                                    </a>
                                    {p.clock_in_accuracy_m != null
                                      ? ` (±${Math.round(p.clock_in_accuracy_m)} m)`
                                      : ""}
                                  </>
                                ) : (
                                  " · no GPS recorded"
                                )}
                                {p.flagged_reason ? ` · ${p.flagged_reason}` : ""}
                              </li>
                            ))}
                          </ul>
                        )}
                        {guardQ.isLoading ? (
                          <Spinner label="Loading scans…" small />
                        ) : (guardQ.data?.scans.length ?? 0) === 0 ? (
                          <p className="text-xs text-[#C0C0C0]">No checkpoint scans this day.</p>
                        ) : (
                          <ul className="space-y-1">
                            {guardQ.data!.scans.map((s) => (
                              <li key={s.id} className="text-xs text-white/85">
                                {time(s.scanned_at)} · {s.checkpoint_name}
                                {s.from_tour ? " · during tour" : " · on-site scan"}
                                {s.distance_m != null ? ` · ${s.distance_m} m` : ""}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {d.incidents.length > 0 && (
            <Section title="Incidents">
              <ul className="divide-y divide-white/10">
                {d.incidents.map((i) => (
                  <li key={i.id} className="p-3 text-sm text-white">
                    {i.title}
                    <span className="ml-2 text-xs text-[#C0C0C0]">
                      {i.severity} · {time(i.occurrence_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
      <h2 className="border-b border-white/10 px-3 py-2 font-[Bebas_Neue] text-lg tracking-wide text-white">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[#C0C0C0]">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-1 font-[Bebas_Neue] text-2xl text-white">{value}</div>
    </div>
  );
}

function Spinner({ label, small }: { label: string; small?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 text-[#C0C0C0]",
        small ? "py-2 text-xs" : "py-12 text-sm",
      )}
    >
      <Loader2 className="h-4 w-4 animate-spin" /> {label}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-sm text-[#C0C0C0]">
      {text}
    </div>
  );
}
